import { theme } from '@traba/theme'
import styled from 'styled-components'
import Row from '../Row'
import { SvgIcon } from '../SvgIcon'

export interface StepperStep {
  /** Unique key for the step (e.g. "build", "eligibility"). */
  id: string
  /** Label shown next to the step number. */
  label: string
}

export interface StepperProps {
  steps: StepperStep[]
  /** Index of the currently-active step (0-based). */
  activeIndex: number
}

/**
 * Linear progress stepper for multi-step wizards. Renders one node per step
 * with a connecting line between them. Steps before `activeIndex` are
 * marked "done" (green check), the step at `activeIndex` is highlighted as
 * "active", and any step after is "pending" (grey).
 *
 * The set of steps can be dynamic — passing a different `steps` array
 * re-renders the stepper. This is how the prereq create-flow wizard
 * conditionally adds a "granting shifts" step when relevant step types
 * are present in the canvas.
 */
export function Stepper({ steps, activeIndex }: StepperProps) {
  return (
    <Container>
      {steps.map((step, idx) => {
        const isDone = idx < activeIndex
        const isActive = idx === activeIndex
        return (
          <Row alignCenter key={step.id} style={{ gap: theme.space.xxs }}>
            <StepNode
              $state={isDone ? 'done' : isActive ? 'active' : 'pending'}
            >
              {isDone ? (
                <SvgIcon name="check" size={12} color={theme.colors.Green70} />
              ) : (
                idx + 1
              )}
            </StepNode>
            <StepLabel
              $state={isDone ? 'done' : isActive ? 'active' : 'pending'}
            >
              {step.label}
            </StepLabel>
            {idx < steps.length - 1 && <Connector />}
          </Row>
        )
      })}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.xs}px;
  padding: 0 ${theme.space.sm}px ${theme.space.xs}px;
  border-bottom: 1px solid ${theme.colors.Grey20};
`

type StepState = 'done' | 'active' | 'pending'

const StepNode = styled.div<{ $state: StepState }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  background: ${({ $state }) =>
    $state === 'active'
      ? theme.colors.Violet60
      : $state === 'done'
        ? theme.colors.Green10
        : theme.colors.Grey10};
  color: ${({ $state }) =>
    $state === 'active'
      ? theme.colors.White
      : $state === 'done'
        ? theme.colors.Green70
        : theme.colors.Grey60};
`

const StepLabel = styled.span<{ $state: StepState }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $state }) =>
    $state === 'active'
      ? theme.colors.MidnightBlue
      : $state === 'done'
        ? theme.colors.Green70
        : theme.colors.Grey60};
`

const Connector = styled.span`
  width: 24px;
  height: 1px;
  background: ${theme.colors.Grey20};
  margin: 0 ${theme.space.xxs}px;
`
